/**
 * /secretariat/formules — Formules & tarifs (Permis B, AAC, A1, A2, etc.)
 * Lecture depuis GET /api/bo/secretariat/formules (statique côté backend).
 */
import React, { useEffect, useState } from 'react';
import { Package, AlertCircle } from 'lucide-react';
import api from '../../api';

const FORMULE_META = {
  permis_b: { label: 'Permis B (voiture)', desc: 'Formation classique 20h min. Boite manuelle ou auto.', color: '#1d4ed8' },
  aac: { label: 'AAC (conduite accompagnée)', desc: '20h min + 3000 km avec accompagnateur. Dès 15 ans.', color: '#059669' },
  permis_b78: { label: 'B78 (boîte auto)', desc: 'Permis B restreint aux véhicules à boite automatique.', color: '#0891b2' },
  permis_a1: { label: 'A1 · 125 cm³', desc: 'Dès 16 ans. Motos 125 cm³ jusqu’à 11 kW.', color: '#f59e0b' },
  permis_a2: { label: 'A2 · Moto intermédiaire', desc: 'Dès 18 ans. Jusqu’à 35 kW / 47.5 ch.', color: '#dc2626' },
  passerelle_a: { label: 'Passerelle A2 → A', desc: '7h de formation après 2 ans de A2.', color: '#7c3aed' },
  formation_125: { label: 'Formation 125', desc: '7h pratique pour conduire 125 cm³ avec permis B ≥ 2 ans.', color: '#ea580c' },
  code_seul: { label: 'Code seul', desc: 'Préparation à l’ETG uniquement, sans conduite.', color: '#64748b' },
};

export default function SecretariatFormulesPage() {
  const [formules, setFormules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Formules & tarifs · Secrétariat';
    (async () => {
      try {
        const r = await api.boGetFormules();
        setFormules(r.formules || []);
      } catch (e) {
        setError(e.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-5xl" data-testid="secretariat-formules-page">
      <div className="mb-6">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-400 mb-1 flex items-center gap-2">
          <Package size={12} /> Secrétariat · Formules
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Formules & tarifs proposés
        </h1>
        <p className="text-sm text-white/60 mt-1">
          Ces formules sont référencées lors de la création d’une fiche élève. Volume horaire, prix et modalités sont configurés dans le back-office.
        </p>
      </div>

      {loading && (
        <div className="text-white/50 text-sm" data-testid="formules-loading">Chargement des formules…</div>
      )}

      {error && (
        <div className="rounded-lg border-2 border-red-800 bg-red-950/30 p-4 text-sm text-red-200 flex items-start gap-2" data-testid="formules-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="formules-grid">
          {formules.map((code) => {
            const meta = FORMULE_META[code] || { label: code, desc: '', color: '#64748b' };
            return (
              <div key={code} className="rounded-xl border-2 border-white/10 bg-white/[0.02] p-4 hover:border-white/20 transition" data-testid={`formule-${code}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: meta.color }}>
                    <Package className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/40">Code : {code}</div>
                    <h2 className="text-base font-black text-white mt-0.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      {meta.label}
                    </h2>
                    <p className="text-xs text-white/60 mt-1">{meta.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 rounded-lg border border-blue-900/50 bg-blue-950/20 p-4 text-xs text-blue-200/80">
        <strong className="text-blue-300">Édition des tarifs :</strong> les prix, durées et packs (20h, 30h, forfait AAC) sont configurés côté backend dans <code className="bg-blue-950/60 px-1 rounded">bo_router.py · FORMULES</code>. Une page d’édition inline arrivera dans une prochaine phase.
      </div>
    </div>
  );
}
